/**
 * RequirementsPage - Top-Level Orchestration Component
 *
 * Purpose: Pure layout and routing component for the workflow.
 * Delegates all state and logic to useWorkflowState hook.
 *
 * Responsibilities:
 * - Render appropriate UI based on workflow step
 * - Orchestrate layout (single panel vs split-screen)
 * - Display error notifications
 * - Route user actions to abstraction layer
 *
 * Modularity Principle: This component has ZERO business logic.
 * All state, validation, and API calls are handled by the abstraction layer.
 */

import type React from "react";
import { RequirementsForm, type RequirementsFormData } from "../components/RequirementsForm";
import { ResultsPanel } from "../components/ResultsPanel";
import { useWorkflowState } from "../hooks/useWorkflowState";

export const RequirementsPage: React.FC = () => {
  // All state and logic comes from the abstraction layer
  const {
    currentStep,
    formData,
    result,
    validation,
    loading,
    error,
    submitRequirements,
    updateRequirements,
    acceptRecommendations,
    resetForm,
    clearError,
  } = useWorkflowState();

  // Debug: Log current step
  console.log("RequirementsPage rendering - currentStep:", currentStep, "formData:", formData);

  // Handle form submission (initial or update)
  const handleFormSubmit = async (_data: RequirementsFormData) => {
    if (currentStep === "results") {
      // Results mode - update existing recommendations
      await updateRequirements();
    } else {
      // Initial mode - submit new requirements
      await submitRequirements();
    }
  };

  // Handle acceptance of recommendations
  const handleAccept = async () => {
    await acceptRecommendations();
  };

  // Determine view mode
  const _isResultsMode = currentStep === "results";
  const _isValidationMode = currentStep === "validation";
  const _isOrchestrationMode = currentStep === "orchestration";

  // referenced to avoid unused local errors under strict checks
  void _isResultsMode;
  void _isValidationMode;
  void _isOrchestrationMode;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Main Content */}
      <main className="relative">
        {/* Error Notification */}
        {error && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-800">
              <strong>Error:</strong> {error.message}
              <button
                type="button"
                onClick={clearError}
                className="ml-4 text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Welcome & Industry & Company Size & Processes & Needs States */}
        {(currentStep === "welcome" ||
          currentStep === "industry" ||
          currentStep === "companySize" ||
          currentStep === "businessProcesses" ||
          currentStep === "specificNeeds") && (
          <div className="min-h-screen flex items-center justify-center p-8">
            <RequirementsForm onSubmit={handleFormSubmit} loading={loading} />
          </div>
        )}

        {/* Results State - Split Panel */}
        {currentStep === "results" && result && (
          <div className="flex min-h-screen">
            {/* Left Panel - Results */}
            <div className="flex-1 p-8">
              <ResultsPanel result={result} onAccept={handleAccept} loading={loading} />
            </div>

            {/* Right Panel - Chat (Fixed Position) */}
            <div className="w-96 p-8">
              <RequirementsForm
                onSubmit={handleFormSubmit}
                loading={loading}
                isResultsMode={true}
                initialData={formData}
              />
            </div>
          </div>
        )}

        {/* Validation State */}
        {currentStep === "validation" && validation && (
          <div className="min-h-screen p-8">
            <div className="max-w-4xl mx-auto bg-white rounded-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Validation Results</h2>

              {validation.isValid ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-6">
                  <div className="text-green-800">
                    <strong>✅ Validation Passed!</strong> Your module selection is compatible and
                    ready for deployment.
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md mb-6">
                  <div className="text-red-800">
                    <strong>❌ Validation Issues Found</strong>
                  </div>
                  {validation.conflicts && validation.conflicts.length > 0 && (
                    <div className="mt-2">
                      <strong>Conflicts:</strong>
                      <ul className="list-disc list-inside">
                        {validation.conflicts.map((conflict, idx) => (
                          <li key={idx}>{conflict}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {validation.missingDependencies && validation.missingDependencies.length > 0 && (
                    <div className="mt-2">
                      <strong>Missing Dependencies:</strong>
                      <ul className="list-disc list-inside">
                        {validation.missingDependencies.map((dep, idx) => (
                          <li key={idx}>{dep}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Orchestration State */}
        {currentStep === "orchestration" && (
          <div className="min-h-screen p-8">
            <div className="max-w-6xl mx-auto bg-white rounded-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                🎉 Deployment Configuration Complete!
              </h2>

              <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-6">
                <div className="text-green-800">
                  Your Odoo configuration is ready. Review the details below or start over to create
                  a new configuration.
                </div>
              </div>

              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Start Over
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
